import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt, faSlidersH, faUser, faUsersCog, faChartLine, faTasks, faHospital, faClipboardList, faBolt, faChevronRight, faChevronLeft, faUserInjured, faMicrophone } from '@fortawesome/free-solid-svg-icons';
import DocumentsListView from './documents/DocumentsListViewController';
import ScribeListViewController from './scribe/list/ScribeListViewController';
import OutcomeTaskViewController from './tasks/OutcomeTaskViewController';
import ConfigViewController from './config/ConfigViewController';
import WorkflowViewController from './workflow/WorkflowViewController';
import ClinicSelectorViewController from './ClinicSelectorViewController';
import { UserAbility, UserViewController, SessionStorage, MAIN_TAB_PAGE_LABEL, NoticeboardProvider, AdminView, IdentityService, TeamService, medPromptBrowserTitle } from '@medprompt/shared';
import InsightsViewController from './insights/InsightsViewController';
import ReferralViewController from './referrals/ReferralViewController';
import PatientSearchViewController from './patients/PatientSearchViewController';
import HelpButton from './help/HelpButton';
import { HELP_PAGE_IDS } from './help/helpContent';
import '@medprompt/shared/src/common/TabView.css';
import { useAuth } from '@medprompt/shared';
import { useLocation } from 'react-router-dom';
import { NotificationService, sharedApiClient } from './fetcher';

// Props will include `setActiveTabLabel` (which is setActiveTabLabel from App.js), `documentViewerContext`, and `taskViewerContext`
const TabViewController = (props) => {
  const location = useLocation();
  
  // Check session storage first, then location state, then default to 'documents'
  const getInitialTab = () => {
    const storedTab = sessionStorage.getItem('lastActivePage');
    return storedTab || location.state?.selectTab || 'documents';
  };
  
  const [activeTabId, setActiveTabId] = useState(getInitialTab());
  const [isExpanded, setIsExpanded] = useState(false);
  const { user } = useAuth();
  const identityService = useMemo(() => new IdentityService(), []);
  const teamService = useMemo(() => new TeamService(), []);
  const fetchClinics = useCallback(() => identityService.fetchClinics(), [identityService]);
  const fetchClinicUsers = useCallback(
    (clinicId) => identityService.fetchClinicUsers({ headers: { 'X-Target-Clinic-Id': clinicId } }),
    [identityService]
  );
  const fetchTeams = useCallback(
    (clinicId) => teamService.fetchTeams({ headers: { 'X-Target-Clinic-Id': clinicId } }),
    [teamService]
  );
  const [notificationCounts, setNotificationCounts] = useState({
    assignedDocumentsCount: 0,
    userConsultationsCount: 0,
    assignedTasksCount: 0
  });
  const [otherClinicsWithNotifications, setOtherClinicsWithNotifications] = useState(0);

  // Save to session storage whenever activeTabId changes
  useEffect(() => {
    sessionStorage.setItem('lastActivePage', activeTabId);
  }, [activeTabId]);

  // Polling for notification counts — re-initialised when clinic changes
  const [clinicTrigger, setClinicTrigger] = useState(0);

  useEffect(() => {
    const onClinicChanged = () => setClinicTrigger(prev => prev + 1);
    window.addEventListener('clinicChanged', onClinicChanged);
    return () => window.removeEventListener('clinicChanged', onClinicChanged);
  }, []);

  useEffect(() => {
    const notificationService = new NotificationService(sharedApiClient);
    
    const fetchNotifications = async () => {
      try {
        const summary = await notificationService.fetchAssignedSummary();
        setNotificationCounts({
          assignedDocumentsCount: summary.assignedDocumentsCount || 0,
          userConsultationsCount: summary.userConsultationsCount || 0,
          assignedTasksCount: summary.assignedTasksCount || 0
        });
      } catch (error) {
        console.error('Error fetching notification summary:', error);
      }

      // Fetch notifications for other clinics to power the clinics tab badge
      const allClinicIds = user?.clinicIds || [];
      const selectedClinicId = SessionStorage.getSelectedClinicId();
      const otherClinicIds = allClinicIds.filter(id => id !== selectedClinicId);

      if (otherClinicIds.length > 0) {
        try {
          const results = await Promise.all(otherClinicIds.map(clinicId =>
            sharedApiClient.get('/api/notification/assigned/fetch', {
              headers: { 'X-Target-Clinic-Id': clinicId }
            }).catch(() => null)
          ));
          const count = results.filter(r =>
            r && ((r.assignedDocumentsCount || 0) > 0
              || (r.userConsultationsCount || 0) > 0
              || (r.assignedTasksCount || 0) > 0)
          ).length;
          setOtherClinicsWithNotifications(count);
        } catch (error) {
          console.error('Error fetching other clinic notifications:', error);
        }
      } else {
        setOtherClinicsWithNotifications(0);
      }
    };

    fetchNotifications();

    const intervalId = setInterval(fetchNotifications, 30000);

    return () => {
      clearInterval(intervalId);
    };
  }, [clinicTrigger, user?.clinicIds]);

  const handleTabClick = (tab) => {
    setActiveTabId(tab.id); // Use new setter name
    if (props.setActiveTabLabel) { // Expecting setActiveTabLabel prop
      props.setActiveTabLabel(tab.label); // Call prop with Label
    }
  };

  const filteredTabs = useMemo(() => {
    const tabs = [
      { id: 'documents', label: MAIN_TAB_PAGE_LABEL.documentsList, icon: faFileAlt, requiredAbility: UserAbility.DOCS_READ.key },
      { id: 'scribe', label: MAIN_TAB_PAGE_LABEL.scribe, icon: faMicrophone, requiredAbility: UserAbility.CONSULT_READ.key },
      { id: 'tasks', label: MAIN_TAB_PAGE_LABEL.outcomeTasks, icon: faTasks, requiredAbility: UserAbility.TASK_READ.key },
      { id: 'referrals', label: MAIN_TAB_PAGE_LABEL.referrals, icon: faClipboardList, requiredAbility: UserAbility.DOCS_READ.key },
      { id: 'patients', label: MAIN_TAB_PAGE_LABEL.patientSearch, icon: faUserInjured, requiredAbility: UserAbility.DOCS_READ.key },
      { id: 'insights', label: MAIN_TAB_PAGE_LABEL.insights, icon: faChartLine, requiredAbility: UserAbility.INSIGHTS_READ.key },
      { id: 'workflows', label: 'Workflows', icon: faBolt, requiredAbility: UserAbility.CONFIG_READ.key },
      { id: 'clinics', label: MAIN_TAB_PAGE_LABEL.clinics, icon: faHospital, visible: () => user?.clinicIds?.length > 1 },
      { id: 'admin', label: MAIN_TAB_PAGE_LABEL.admin, icon: faUsersCog, requiredAbility: UserAbility.PERMS_READ.key },
      { id: 'user', label: MAIN_TAB_PAGE_LABEL.user, icon: faUser },
      { id: 'config', label: MAIN_TAB_PAGE_LABEL.configuration, icon: faSlidersH, requiredAbility: UserAbility.CONFIG_WRITE.key }
    ];
    return tabs.filter(tab => {
      if (tab.visible && !tab.visible()) return false;
      if (!tab.requiredAbility) return true;
      return user?.authorities?.includes(tab.requiredAbility);
    });
  }, [user?.authorities, user?.clinicIds]);

  useEffect(() => {
    if (filteredTabs.length === 0) {
      return;
    }
    if (!filteredTabs.some(tab => tab.id === activeTabId)) {
      const fallbackTab = filteredTabs[0];
      setActiveTabId(fallbackTab.id);
      if (props.setActiveTabLabel) {
        props.setActiveTabLabel(fallbackTab.label);
      }
    }
  }, [filteredTabs, activeTabId, props.setActiveTabLabel]);

  return (
    <NoticeboardProvider autoPresent>
      <div className="tab-view-container">
      <div className={`sidebar ${isExpanded ? 'expanded' : ''}`}>
        <div 
          className={`sidebar-toggle ${isExpanded ? 'expanded' : ''}`}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="sidebar-toggle-icon">
            <FontAwesomeIcon icon={isExpanded ? faChevronLeft : faChevronRight} />
          </div>
          {isExpanded && <span className="sidebar-toggle-label">{isExpanded ? 'Collapse Menu' : 'Expand Menu'}</span>}
          {!isExpanded && <span className="toggle-tooltip">{isExpanded ? 'Collapse Menu' : 'Expand Menu'}</span>}
        </div>
        {filteredTabs.map(tab => (
          <div
            key={tab.id}
            className={`sidebar-tab ${activeTabId === tab.id ? 'active' : ''} ${isExpanded ? 'expanded' : ''}`}
            onClick={() => handleTabClick(tab)}
          >
            <div className="sidebar-tab-icon">
              <FontAwesomeIcon icon={tab.icon} />
              {tab.id === 'documents' && notificationCounts.assignedDocumentsCount > 0 && (
                <span className="notification-count-badge">{notificationCounts.assignedDocumentsCount}</span>
              )}
              {tab.id === 'scribe' && notificationCounts.userConsultationsCount > 0 && (
                <span className="notification-count-badge">{notificationCounts.userConsultationsCount}</span>
              )}
              {tab.id === 'tasks' && notificationCounts.assignedTasksCount > 0 && (
                <span className="notification-count-badge">{notificationCounts.assignedTasksCount}</span>
              )}
              {tab.id === 'clinics' && otherClinicsWithNotifications > 0 && (
                <span className="notification-count-badge">{otherClinicsWithNotifications}</span>
              )}
            </div>
            {isExpanded && <span className="sidebar-tab-label">{tab.label}</span>}
            {!isExpanded && <span className="sidebar-tab-tooltip">{tab.label}</span>}
          </div>
        ))}
      </div>
      <div className="content-area">
        {activeTabId === 'documents' && <DocumentsListView viewerContext={props.documentViewerContext} />}
        {activeTabId === 'scribe' && <ScribeListViewController />}
        {activeTabId === 'tasks' && <OutcomeTaskViewController taskViewerContext={props.taskViewerContext} />}
        {activeTabId === 'referrals' && <ReferralViewController />}
        {activeTabId === 'patients' && <PatientSearchViewController />}
        {activeTabId === 'insights' && <InsightsViewController isSidebarExpanded={isExpanded} />}
        {activeTabId === 'workflows' && <WorkflowViewController />}
        {activeTabId === 'clinics' && <ClinicSelectorViewController />}
        {activeTabId === 'admin' && (
          <AdminView
            variant="clinic"
            isSidebarExpanded={isExpanded}
            title={MAIN_TAB_PAGE_LABEL.admin}
            documentTitle={medPromptBrowserTitle(MAIN_TAB_PAGE_LABEL.admin)}
            titleAccessory={<HelpButton pageId={HELP_PAGE_IDS.admin} />}
            showViewerSubtitle
            fetchClinics={fetchClinics}
            fetchClinicUsers={fetchClinicUsers}
            fetchTeams={fetchTeams}
          />
        )}
        {activeTabId === 'user' && (
          <UserViewController titleActions={<HelpButton pageId={HELP_PAGE_IDS.user} />} />
        )}
        {activeTabId === 'config' && 
          <ConfigViewController 
            isSidebarExpanded={isExpanded}
            onClose={() => {
              setActiveTabId('documents'); // Use new setter name
              if (props.setActiveTabLabel) { // Expecting setActiveTabLabel prop
                props.setActiveTabLabel(MAIN_TAB_PAGE_LABEL.documentsList);
              }
            }}
          />}
      </div>
      </div>
    </NoticeboardProvider>
  );
};

export default TabViewController; 